import React, { useState } from 'react';
import {
  Box, Collapse, Link, Stack, Typography,
} from '@mui/material';
import BotAvatar from './BotAvatar';
import RichText from './RichText';
import ApprovalContent from './ApprovalContent';
import { formatDateTime } from '../utils/format';
import { tokens } from '../theme';
import ChatService from '../services/ChatService';

const KIND_LABEL = {
  plan: 'Planned the work',
  bot_reply: 'Answered',
  tool_call: 'Looked something up',
  tool_denied: 'Was blocked',
  synthesis: 'Wrote the summary',
};

function stepLine(s) {
  const tool = s.input?.tool;
  const names = {
    get_recent_orders: 'recent orders',
    get_low_stock: 'low stock',
    propose_send_email: 'an email proposal',
    get_social_metrics: 'social media metrics',
    get_campaign_metrics: 'campaign metrics',
    list_social_accounts: 'connected accounts',
    ask_teammate: `a teammate (${s.input?.to || s.input?.name || ''})`,
    get_team_overview: 'the team overview',
    propose_create_team: 'a team proposal',
    propose_create_bot: 'a team member proposal',
    propose_change_reporting: 'a reporting-lines proposal',
  };
  const what = tool ? (names[tool] || tool) : '';
  const err = s.output?.error ? ` (${s.output.error})` : '';
  return `${s.bot || 'Team'}: ${KIND_LABEL[s.kind] || s.kind}${what ? ` ${what}` : ''}${err}`;
}

/** Collapsible list of what the team did for a message (loaded when opened). */
function Steps({ runId }) {
  const [open, setOpen] = useState(false);
  const [steps, setSteps] = useState(null);
  const [failed, setFailed] = useState(false);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !steps) {
      try { setSteps((await ChatService.run(runId)).steps); } catch (e) { setFailed(true); }
    }
  };
  return (
    <Box sx={{ mt: 0.5 }}>
      <Link component="button" type="button" variant="caption" underline="hover" onClick={toggle} color="text.secondary">
        {open ? 'Hide what it did' : 'What it did'}
      </Link>
      <Collapse in={open}>
        <Box sx={{ mt: 0.5, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
          {failed && <Typography variant="caption" color="error">Could not load the steps.</Typography>}
          {!steps && !failed && <Typography variant="caption" color="text.secondary">Loading...</Typography>}
          {steps && steps.map((s) => <Typography key={s.seq} variant="caption" display="block" color="text.secondary">{stepLine(s)}</Typography>)}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * One chat message. Bot messages: avatar, coloured name, grey bubble. Owner messages: blue bubble on the right.
 * Status notes are centred; approval cards show the live approval with its buttons.
 */
export default function MessageBubble({
  message, showHeader, isMine, isOwner, approvalActions, showSteps,
}) {
  if (message.kind === 'status') {
    const warn = Boolean(message.meta?.stop_reason);
    return (
      <Box sx={{ textAlign: 'center', my: 1.5, px: 2 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'inline-block', px: 1.5, py: 0.5, borderRadius: 4, bgcolor: warn ? tokens.warningSoft : 'action.hover', color: warn ? tokens.warning : 'text.secondary',
          }}
        >
          {message.content}
        </Typography>
      </Box>
    );
  }

  const bot = message.sender?.bot;
  if (isMine) {
    return (
      <Stack alignItems="flex-end" sx={{ my: 0.5 }}>
        {message.meta?.schedule_id && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
            {`${message.meta.source === 'trigger' ? 'Triggered' : 'Scheduled'}${message.meta.schedule_title ? `: ${message.meta.schedule_title}` : ''}${message.meta.manual ? (message.meta.source === 'trigger' ? ' (checked now)' : ' (run now)') : ''}`}
          </Typography>
        )}
        <Box sx={{
          maxWidth: '78%', bgcolor: message.pending ? 'primary.light' : 'primary.main', color: 'primary.contrastText', borderRadius: 3, borderBottomRightRadius: 6, px: 1.75, py: 1,
        }}
        >
          <RichText text={message.content} />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>{message.pending ? 'Sending...' : formatDateTime(message.created_at)}</Typography>
      </Stack>
    );
  }

  const card = message.kind === 'approval_card';
  return (
    <Stack direction="row" spacing={1.25} sx={{ my: 0.5 }} alignItems="flex-end">
      <Box sx={{ width: 36, flexShrink: 0 }}>{showHeader && <BotAvatar bot={bot} size={36} />}</Box>
      <Box sx={{ maxWidth: card ? '92%' : '78%', minWidth: 0, flexGrow: card ? 1 : 0 }}>
        {showHeader && (
          <Typography variant="caption" fontWeight={600} sx={{ color: bot?.avatar_color || 'text.secondary', ml: 0.5 }}>
            {bot?.name || 'Team'}
          </Typography>
        )}
        {card && message.approval ? (
          <ApprovalContent
            framed
            approval={message.approval}
            isOwner={isOwner}
            busy={approvalActions.busyId === message.approval.id}
            onApprove={(grants) => approvalActions.approve(message.approval.id, grants)}
            onReject={() => approvalActions.reject(message.approval.id)}
          />
        ) : (
          <Box sx={{ bgcolor: '#f0f2f5', borderRadius: 3, borderBottomLeftRadius: 6, px: 1.75, py: 1 }}>
            <RichText text={message.content} />
          </Box>
        )}
        {showSteps && message.run_id && !card && <Steps runId={message.run_id} />}
      </Box>
    </Stack>
  );
}
